import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../utils/AuthContext';
import { listBookingsForRange, updateBooking } from '../utils/db/bookings';
import CustomerAutocomplete from '../components/CustomerAutocomplete';

const BulkModify = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ customerId: '', fromDate: '', toDate: '' });
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({}); // id -> true
  const [edited, setEdited] = useState({}); // id -> editable row copy
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [transferTargetCustomer, setTransferTargetCustomer] = useState(null);

  const allSelected = useMemo(() =>
    rows.length > 0 && rows.every(r => selected[r.id]),
    [rows, selected]);

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    rows.forEach(r => { next[r.id] = true; });
    setSelected(next);
  };

  const fetchRows = async () => {
    if (!user) return;
    if (!filters.customerId || !filters.fromDate || !filters.toDate) {
      toast.error('Please enter Customer ID, From Date and To Date');
      setRows([]);
      setEdited({});
      setSelected({});
      setHasSearched(false);
      return;
    }
    setIsLoading(true);
    try {
      const list = await listBookingsForRange(user.id, filters.customerId || null, filters.fromDate || null, filters.toDate || null);
      setRows(list);
      // seed edited map with transformed rows
      const seeded = {};
      list.forEach(r => {
        seeded[r.id] = {
          awbNo: r.awb_no,
          customerId: r.customer_id,
          date: r.date,
          receiverName: r.receiver_name,
          receiverAddress: r.receiver_address || '',
          pincode: r.pincode || '',
          mode: r.mode,
          type: r.type || '',
          weight: r.weight,
          quantity: r.quantity,
          amount: r.amount,
          otherCharge: r.other_charge || 0,
        };
      });
      setEdited(seeded);
      setSelected({});
      setHasSearched(true);
    } catch (e) {
      toast.error('Failed to fetch bookings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (id, field, value) => {
    setEdited(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const saveSelected = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (ids.length === 0) {
      toast.error('Select at least one booking');
      return;
    }
    if (!user) return;
    setSaving(true);
    try {
      for (const id of ids) {
        const payload = edited[id];
        await updateBooking(user.id, id, payload);
      }
      toast.success(`Updated ${ids.length} booking(s)`);
      await fetchRows();
    } catch (e) {
      toast.error('Failed to update selected bookings');
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async () => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (ids.length === 0) {
      toast.error('Select at least one booking to transfer');
      return;
    }
    if (!transferTargetCustomer) {
      toast.error('Select a target customer for transfer');
      return;
    }
    if (!window.confirm(`Are you sure you want to transfer ${ids.length} booking(s) to ${transferTargetCustomer.customer_name}?`)) {
      return;
    }

    setSaving(true);
    try {
      // We only update the customer_id (UUID), but updateBooking requires a full payload
      // because it converts input using toDb() which constructs a complete object.

      for (const id of ids) {
        const currentData = edited[id];
        const payload = { ...currentData, customer_id: transferTargetCustomer.id };
        await updateBooking(user.id, id, payload);
      }
      toast.success(`Transferred ${ids.length} booking(s) successfully`);
      // Clear selection and target
      setTransferTargetCustomer(null);
      // Refresh rows - transferred rows should disappear from current filtered list
      await fetchRows();
    } catch (e) {
      console.error("Transfer error", e);
      toast.error('Failed to transfer bookings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bulk Modify Bookings</h1>
        <p className="text-gray-600">Filter bookings, edit inline, select multiple, and save changes together.</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <CustomerAutocomplete
            label="Customer ID *"
            placeholder="Type to search by Customer ID"
            onChange={(c) => {
              setSelectedCustomer(c || null);
              setFilters({ ...filters, customerId: c?.id || '' });
            }}
            value={selectedCustomer?.id}
            valueLabel={selectedCustomer ? `${selectedCustomer.customer_id} - ${selectedCustomer.customer_name}` : ''}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">From Date *</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
            onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">To Date *</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
            onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div className="flex items-end gap-2">
          <button onClick={fetchRows} disabled={isLoading || !filters.customerId || !filters.fromDate || !filters.toDate} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 w-full disabled:opacity-50 disabled:cursor-not-allowed">{isLoading ? 'Loading...' : 'Show'}</button>
        </div>
      </div>

      {/* Table */}
      {hasSearched && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3"><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">AWB</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receiver</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pincode</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Other</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r) => {
                const e = edited[r.id] || {};
                return (
                  <tr key={r.id} className="align-top">
                    <td className="px-3 py-2"><input type="checkbox" checked={!!selected[r.id]} onChange={(ev) => setSelected(prev => ({ ...prev, [r.id]: ev.target.checked }))} /></td>
                    <td className="px-3 py-2 text-sm text-gray-900 whitespace-nowrap">{r.awb_no}</td>
                    <td className="px-3 py-2"><input type="date" value={e.date || ''} onChange={(ev) => handleChange(r.id, 'date', ev.target.value)} onClick={(ev) => { try { if (ev.currentTarget.showPicker) ev.currentTarget.showPicker(); } catch (_) { } }} className="w-36 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2">
                      <span className="text-sm text-gray-900 truncate max-w-[16rem]" title={r.customer_name || r.customer_id}>{r.customer_name || r.customer_id}</span>
                    </td>
                    <td className="px-3 py-2"><input type="text" value={e.receiverName || ''} onChange={(ev) => handleChange(r.id, 'receiverName', ev.target.value.slice(0, 100))} maxLength={100} className="w-36 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2"><input type="text" value={e.receiverAddress || ''} onChange={(ev) => handleChange(r.id, 'receiverAddress', ev.target.value.slice(0, 200))} maxLength={200} className="w-48 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2"><input type="text" value={e.pincode || ''} onChange={(ev) => handleChange(r.id, 'pincode', ev.target.value.replace(/[^0-9]/g, '').slice(0, 6))} maxLength={6} className="w-24 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2">
                      <select value={e.mode || ''} onChange={(ev) => handleChange(r.id, 'mode', (ev.target.value || '').slice(0, 20))} className="w-28 px-2 py-1 border rounded">
                        <option value="">Select Mode</option>
                        <option value="Air">Air</option>
                        <option value="Road">Road</option>
                        <option value="Express">Express</option>
                        <option value="Standard">Standard</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select value={e.type || ''} onChange={(ev) => handleChange(r.id, 'type', (ev.target.value || '').slice(0, 20))} className="w-32 px-2 py-1 border rounded">
                        <option value="">Select Type</option>
                        <option value="Doc">Document</option>
                        <option value="Non-Doc">Non-Document</option>
                        <option value="Fragile">Fragile</option>
                        <option value="Heavy">Heavy</option>
                      </select>
                    </td>
                    <td className="px-3 py-2"><input type="number" step="0.01" value={e.weight ?? ''} onChange={(ev) => handleChange(r.id, 'weight', ev.target.value)} className="w-24 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2"><input type="number" value={e.quantity ?? ''} onChange={(ev) => handleChange(r.id, 'quantity', ev.target.value)} className="w-20 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2"><input type="number" step="0.01" value={e.amount ?? ''} onChange={(ev) => handleChange(r.id, 'amount', ev.target.value)} className="w-24 px-2 py-1 border rounded" /></td>
                    <td className="px-3 py-2"><input type="number" step="0.01" value={e.otherCharge ?? ''} onChange={(ev) => handleChange(r.id, 'otherCharge', ev.target.value)} className="w-24 px-2 py-1 border rounded" /></td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td className="px-6 py-4 text-center text-gray-500" colSpan="13">No bookings</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-end gap-3 w-1/2">
          <div className="flex-1">
            <CustomerAutocomplete
              label="Transfer Selection To Customer"
              placeholder="Search Target Customer"
              onChange={(c) => {
                setTransferTargetCustomer(c || null);
              }}
              value={transferTargetCustomer?.id}
              valueLabel={transferTargetCustomer ? `${transferTargetCustomer.customer_id} - ${transferTargetCustomer.customer_name}` : ''}
            />
          </div>
          <button
            onClick={handleTransfer}
            disabled={saving || !transferTargetCustomer || Object.keys(selected).filter(k => selected[k]).length === 0}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 mb-[0.2rem]"
          >
            {saving ? 'Transferring...' : 'Transfer'}
          </button>
        </div>

        <button onClick={saveSelected} disabled={saving || Object.keys(selected).filter(k => selected[k]).length === 0} className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default BulkModify;


